import {
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  Flex,
  ModalCloseButton,
  Spinner,
  ModalBody,
  ModalFooter,
  Button,
} from '@chakra-ui/react';
import { samplesApi } from '@texas/api/endpoints/samplesApi';
import { useApiRequest } from '@texas/api/hooks/useApiRequest';
import { useApiResource } from '@texas/api/hooks/useApiResource';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { request } from '@texas/utils/helpers/httpHelpers';
import { SamplesTable } from './SamplesTable';
import { ErrorDetails } from '../alert/ErrorDetails';
import { SharedDisclosureProps } from '../types';

interface Props extends SharedDisclosureProps {
  sampleIds: number[];
  onReceived: () => void;
}

export function SetSamplesAsReceivedFromSupplierModal(props: Props) {
  const { t } = useTranslation();

  return (
    <Modal isOpen={props.isOpen} onClose={props.onClose} size="4xl">
      <ModalOverlay />
      <ModalContent>
        <ModalHeader>{t('sample.setReceivedFromSupplier')}</ModalHeader>
        <ModalCloseButton />
        <Inner
          sampleIds={props.sampleIds}
          onReceived={() => {
            props.onReceived();
            props.onClose();
          }}
        />
      </ModalContent>
    </Modal>
  );
}

function Inner({
  sampleIds,
  onReceived,
}: {
  sampleIds: number[];
  onReceived: () => void;
}) {
  const { t } = useTranslation();
  const [receivedSamples, setReceivedSamples] = useState<{
    samples: { sampleId: number; supplierItemCode: string }[];
  }>({ samples: [] });

  const {
    data: samples,
    refetch: refetchSamples,
    loading: samplesLoading,
    error,
  } = useApiResource(samplesApi.fromIds);

  const {
    request: setAsReceivedRequest,
    loading: sentLoading,
    error: sentError,
  } = useApiRequest(samplesApi.setAsUnknownShipmentReceived);

  useEffect(() => {
    refetchSamples(sampleIds);
  }, [refetchSamples, sampleIds]);

  useEffect(() => {
    if (samples) {
      setReceivedSamples({
        samples: samples.map((s) => ({
          sampleId: s.sampleId,
          supplierItemCode: s.productApproval.externalSupplierItemCode,
        })),
      });
    } else {
      setReceivedSamples({ samples: [] });
    }
  }, [setReceivedSamples, samples]);

  const handleChangeSupplierItemCode = (
    sampleId: number,
    supplierItemCode: string,
  ) => {
    setReceivedSamples((prev) => ({
      samples: prev.samples.map((s) =>
        s.sampleId === sampleId ? { ...s, supplierItemCode } : s,
      ),
    }));
  };

  const sentRequest = () =>
    request(setAsReceivedRequest, [receivedSamples], () => onReceived());

  return (
    <>
      <ModalBody>
        <Flex flexDir="column" gap={2} w="full">
          {error && <ErrorDetails error={error} />}
          {sentError && <ErrorDetails error={sentError} />}
          {samplesLoading && <Spinner />}
          <SamplesTable
            samples={samples ?? []}
            onChangeSupplierItemCode={handleChangeSupplierItemCode}
          />
        </Flex>
      </ModalBody>
      <ModalFooter>
        <Button
          isLoading={sentLoading}
          disabled={sentLoading}
          onClick={() => sentRequest()}
        >
          {t('general.confirm')}
        </Button>
      </ModalFooter>
    </>
  );
}
